import { isDirectLineHealthy } from "../directLlineClient";
import {
  addMessage,
  removeMessageByClientId,
  removeMessageByType,
  setBootstrapError,
  setBootstrapInitialized,
} from "./chatSlice";
import { chatModuleState, resetChatModuleState } from "./chatModuleState";
import { initializeChatListener } from "./chatListener";

const SUCCESS_MESSAGE_TTL_MS = 3000;

/**
 * Transparently reconnect DirectLine when the connection has died.
 *
 * Performs the full reconnection cycle:
 * 1. Reset module-level state (listener, subscription, DirectLine instance)
 * 2. Reinitialize chat listener (new token, new connection, new subscription, health probe, bootstrap)
 *
 * Uses a mutex to prevent concurrent reconnections.
 * NOTE: Creates a NEW conversation — bot won't have previous message history,
 * but bootstrap re-sends project context.
 */
export const reconnectDirectLine =
  () =>
  async (dispatch: any): Promise<void> => {
    // Mutex: if reconnection already in progress, piggyback on that promise
    if (chatModuleState.reconnectionInProgress !== null) {
      await chatModuleState.reconnectionInProgress;
      return;
    }

    let resolveReconnection!: () => void;
    let rejectReconnection!: (err: Error) => void;

    chatModuleState.reconnectionInProgress = new Promise<void>((resolve, reject) => {
      resolveReconnection = resolve;
      rejectReconnection = reject;
    });

    try {
      // Show reconnection feedback to user
      dispatch(
        addMessage({
          sender: "system",
          text: "Reconnecting to chat service...",
          type: "reconnection-status",
        })
      );

      await resetChatModuleState();
      dispatch(setBootstrapInitialized(false));
      dispatch(setBootstrapError(null));
      await dispatch(initializeChatListener());

      if (!isDirectLineHealthy()) {
        throw new Error("DirectLine reconnection completed but connection is not healthy");
      }

      announceReconnected(dispatch);
      resolveReconnection();
    } catch (err) {
      console.error("❌ [RECONNECT] Reconnection failed:", err);

      dispatch(removeMessageByType("reconnection-status"));
      dispatch(
        addMessage({
          sender: "system",
          text: "Failed to reconnect to chat service. Please refresh the page.",
          type: "reconnection-error",
        })
      );

      rejectReconnection(err as Error);
      throw err;
    } finally {
      chatModuleState.reconnectionInProgress = null;
    }
  };

/** Replace the status message with a success note that clears itself. */
function announceReconnected(dispatch: any): void {
  dispatch(removeMessageByType("reconnection-status"));

  const successMessageId = `reconnection-success-${Date.now()}`;
  dispatch(
    addMessage({
      sender: "system",
      text: "Reconnected successfully. Your message is being sent.",
      type: "reconnection-success",
      clientMessageId: successMessageId,
    })
  );

  // Auto-remove this specific success message after 3 seconds
  setTimeout(() => {
    dispatch(removeMessageByClientId(successMessageId));
  }, SUCCESS_MESSAGE_TTL_MS);
}
